/**
 * preview-channel — the pure postMessage contract with the embedded pop.in app,
 * extracted from the React frame so the v2/legacy decision is unit-testable.
 *
 * Two channels:
 *   • legacy `popin:theme-preview` — the 6 pre-computed hexes (+ logo/terminology).
 *   • v2 `popin:theme-preview-v2` — the RECIPE (treatment/seed/polarity/font); a
 *     v2-aware embed regenerates the full theme with the real engine.
 *
 * Graceful fallback: until an embed ACKs v2 we send BOTH (v2 + legacy), so a
 * v2-unaware build still previews via the legacy message. Once it acks, v2 only.
 */

import type { PinVerdict, PinVerdictReport } from "@/data/pin-verdicts";

/** postMessage type tags (see popin-rn useBrandTheme; v2 listener lands in the token-map issue). */
export const PREVIEW_MESSAGE_TYPE = "popin:theme-preview";
export const PREVIEW_READY_TYPE = "popin:preview-ready";
export const PREVIEW_MESSAGE_TYPE_V2 = "popin:theme-preview-v2";
export const PREVIEW_V2_ACK_TYPE = "popin:theme-preview-v2-ack";

/** The theme RECIPE a v2-aware embed regenerates from (ids/scalars, not colours). */
export interface ThemeRecipePayload {
  treatment: string;
  seed: string;
  polarity: "light" | "dark";
  fontId: string;
}

/** The unsaved draft pushed into the embed on every edit. */
export interface ThemePreview {
  name: string;
  colors: {
    background: string;
    surface: string;
    primary: string;
    accent: string;
    text: string;
    header: string;
  };
  logoText: string;
  logoImg: string | null;
  terminology: {
    currencySingular: string;
    currencyPlural: string;
    currencyIcon: string;
    walletLabel: string;
    rewardLabel: string;
  };
  /** When set, the preview is treatment-driven (v2); absent → raw-hex/legacy only. */
  recipe?: ThemeRecipePayload;
}

export interface PreviewMessage {
  type: string;
  [key: string]: unknown;
}

/**
 * `pinned` (product#116): the v2 ack is `{ type: PREVIEW_V2_ACK_TYPE, pinVerdicts }`
 * (popin-rn#1379, utils/useBrandTheme.tsx @68879ba0). We send no `pins`, so the
 * app pins the recipe seed as `primary`; a row exists only for a pin that was
 * given, and pop.in's own Night × #9528FD sends `[]`.
 */
function toPinVerdict(raw: unknown): PinVerdict | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.pin !== "primary" && r.pin !== "accent") return null;
  if (r.placed === true && typeof r.value === "string") return { pin: r.pin, placed: true, value: r.value };
  if (r.placed === false && typeof r.reason === "string") return { pin: r.pin, placed: false, reason: r.reason };
  return null;
}

/** The verdicts on a v2 ack. Defensive: a missing or non-array `pinVerdicts`
 *  is no verdicts, and an entry of unknown shape is dropped. */
export function parsePinVerdicts(ack: unknown): PinVerdict[] {
  const list = (ack as { pinVerdicts?: unknown } | null | undefined)?.pinVerdicts;
  if (!Array.isArray(list)) return [];
  return list.map(toPinVerdict).filter((v): v is PinVerdict => v !== null);
}

/** The frame's side of the verdict exchange. */
export interface PinVerdictState {
  /** The colours of the latest v2 post (null: none, or the latest was legacy). */
  latest: ThemePreview["colors"] | null;
  /** Whether the latest draft's answer is still owed: a v2 post is scheduled
   *  (inside the debounce) or sent and not yet acked. */
  pending: "none" | "scheduled" | "sent";
  /** The latest ack, paired with the draft it judged. */
  report: PinVerdictReport | null;
}

export const NO_PIN_VERDICTS: PinVerdictState = { latest: null, pending: "none", report: null };

/** True while a save should wait for the app's answer on the latest draft. */
export const isAwaiting = (s: PinVerdictState): boolean => s.pending !== "none";

export type PinVerdictEvent =
  | { type: "scheduled"; v2: boolean }
  | { type: "sent"; preview: ThemePreview; v2: boolean }
  | { type: "ack"; data: unknown }
  | { type: "reset" };

/**
 * Pair each ack with the draft it judged. The ack carries no id, so only the
 * LATEST v2 post is kept (a new post replaces it; nothing queues) and every ack
 * is paired with it. The app acks in order, so the last ack to arrive answers
 * the latest post: a late ack for an older post is overwritten by the latest
 * post's own ack, and a post the app never acks (unknown treatment, bad seed)
 * can't wedge later ones. An ack with no v2 post on record is dropped.
 */
export function reducePinVerdicts(state: PinVerdictState, event: PinVerdictEvent): PinVerdictState {
  switch (event.type) {
    case "scheduled":
      return { ...state, pending: event.v2 ? "scheduled" : "none" };
    case "sent":
      return event.v2
        ? { ...state, latest: event.preview.colors, pending: "sent" }
        : { ...state, latest: null, pending: "none" };
    case "ack":
      if (!state.latest) return state;
      return {
        latest: state.latest,
        // An ack inside the debounce answers an earlier post; the scheduled one is still owed.
        pending: state.pending === "sent" ? "none" : state.pending,
        report: { colors: state.latest, verdicts: parsePinVerdicts(event.data) },
      };
    case "reset":
      return NO_PIN_VERDICTS;
  }
}

/**
 * Decide which message(s) to post for a preview, given whether this embed has
 * acked v2. Pure — the frame just posts the returned messages to PLAY_ORIGIN.
 *
 *   • no recipe            → [legacy]                (raw-hex edits / terminology)
 *   • recipe, not v2-acked → [v2, legacy]            (fallback: old embed still previews)
 *   • recipe, v2-acked     → [v2]                    (embed regenerates from the recipe)
 */
export function buildPreviewMessages(
  preview: ThemePreview,
  v2Acked: boolean,
): { channel: "legacy" | "v2" | "v2+legacy"; messages: PreviewMessage[] } {
  const { recipe, ...rest } = preview;
  if (!recipe) {
    return { channel: "legacy", messages: [{ type: PREVIEW_MESSAGE_TYPE, ...rest }] };
  }
  const messages: PreviewMessage[] = [{ type: PREVIEW_MESSAGE_TYPE_V2, ...rest, ...recipe }];
  if (!v2Acked) messages.push({ type: PREVIEW_MESSAGE_TYPE, ...rest });
  return { channel: v2Acked ? "v2" : "v2+legacy", messages };
}
